import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, EyeOff, X, Users, ChevronDown, Settings, Download } from 'lucide-react';
import ReportService from '../services/ReportService';
import Table from '../components/ui/Table';
import Modal from '../components/ui/Modal';
import ConfirmModal from '../components/ui/ConfirmModal';
import SearchableDropdown from '../components/SearchableDropdown';
import { useToast } from '../contexts/ToastContext';
import { useSelector } from 'react-redux';
import APIRequest from '../api/APIRequest';
import ItemsPerPageSelector from '../components/ui/ItemsPerPageSelector';
import { studentService } from '../services/studentService';
import { classService } from '../services/classService';
import { fieldConfigService } from '../services/fieldConfigService';
import DynamicFields from '../components/forms/DynamicFields';
import CustomDatePicker from '../components/ui/CustomDatePicker';
import PhoneInput from '../components/ui/PhoneInput';



const Students = () => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { user } = useSelector((state) => state.auth);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('studentItemsPerPage');
    return saved ? JSON.parse(saved) : 10;
  });

  const handleItemsPerPageChange = (newValue) => {
    setItemsPerPage(newValue);
    setCurrentPage(1); // Reset to first page
    localStorage.setItem('studentItemsPerPage', JSON.stringify(newValue));
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode] = useState('list');
  const [isCreating, setIsCreating] = useState(false);

  // Filter states
  const [nameSort, setNameSort] = useState('a-z'); // 'a-z' or 'z-a'
  const [selectedClassFilter, setSelectedClassFilter] = useState('');
  const [selectedGenderFilter, setSelectedGenderFilter] = useState('');
  const [selectedAcademicYearFilter, setSelectedAcademicYearFilter] = useState('');

  // Modal protection states
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [historyStateAdded, setHistoryStateAdded] = useState(false);

  // Status toggle states
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [studentToToggle, setStudentToToggle] = useState(null);
  const [targetStatus, setTargetStatus] = useState('');

  const handleResetColumns = () => {
    // Hidden
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '+91 ',
    class: '',
    academicYear: '',
    dateOfBirth: '',
    contactPerson: [{ relation: 'Father', phone: '+91 ', name: '' }],
    gender: 'male',
    motherLanguage: '',
    nationality: 'INDIA',
    aadharNumber: '',
    birthCertificateNumber: '',
    bloodGroup: '',
    address: ''
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [selectedClassFees, setSelectedClassFees] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [configFields, setConfigFields] = useState([]);

  // Fetch student dynamic fields
  useEffect(() => {
    const fetchStudentFields = async () => {
      try {
        const fields = await fieldConfigService.getEntityFields('student', user.token);
        setConfigFields(Array.isArray(fields) ? fields : []);
      } catch (err) {
        console.error('Error fetching student fields:', err);
      }
    };
    if (user.token) {
      fetchStudentFields();
    }
  }, [user.token]);

  // Handle custom field changes
  const handleCustomFieldChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      customFields: {
        ...(prev.customFields || {}),
        [name]: value
      }
    }));
  };

  // Function to check if form has unsaved changes
  const hasUnsavedChanges = React.useCallback(() => {
    const initialFormData = {
      name: '',
      email: '',
      phone: '+91',
      class: '',
      academicYear: '',
      dateOfBirth: '',
      contactPerson: [{ relation: '', phone: '+91', name: '' }],
      gender: 'male',
      motherLanguage: '',
      nationality: 'INDIA',
      aadharNumber: '',
      birthCertificateNumber: '',
      bloodGroup: '',
      address: '',
      password: ''
    };
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData]);

  // Get unique academic years from classes
  const getUniqueAcademicYears = () => {
    const academicYears = new Set();
    classes.forEach(cls => {
      if (cls.academicYearFrom && cls.academicYearTo) {
        const fromYear = cls.academicYearFrom.toString().length <= 4 
          ? cls.academicYearFrom 
          : new Date(cls.academicYearFrom).getFullYear();
        const toYear = cls.academicYearTo.toString().length <= 4 
          ? cls.academicYearTo 
          : new Date(cls.academicYearTo).getFullYear();
        const academicYear = `${fromYear}-${toYear}`;
        academicYears.add(academicYear);
      }
    });
    return Array.from(academicYears).sort();
  };

  // Get classes filtered by academic year
  const getFilteredClasses = () => {
    if (!formData.academicYear) return classes;

    return classes.filter(cls => {
      if (!cls.academicYearFrom || !cls.academicYearTo) return false;
      const fromYear = cls.academicYearFrom.toString().length <= 4 
        ? cls.academicYearFrom 
        : new Date(cls.academicYearFrom).getFullYear();
      const toYear = cls.academicYearTo.toString().length <= 4 
        ? cls.academicYearTo 
        : new Date(cls.academicYearTo).getFullYear();
      const academicYear = `${fromYear}-${toYear}`;
      return academicYear === formData.academicYear;
    });
  };

  // Get classes filtered by selected academic year for main view
  const getClassesForSelectedAcademicYear = () => {
    if (!selectedAcademicYearFilter) return classes;

    return classes.filter(cls => {
      if (!cls.academicYearFrom || !cls.academicYearTo) return false;
      const fromYear = new Date(cls.academicYearFrom).getFullYear();
      const toYear = new Date(cls.academicYearTo).getFullYear();
      const academicYear = `${fromYear}-${toYear}`;
      return academicYear === selectedAcademicYearFilter;
    });
  };

  // Get class fees when class is selected
  const handleClassSelection = (selectedClass) => {
    setFormData({ ...formData, class: selectedClass });

    if (selectedClass && selectedClass._id) {
      // Find the full class data with fees information
      const fullClassData = classes.find(cls => cls._id === selectedClass._id);
      if (fullClassData) {
        setSelectedClassFees({
          feesPerAcademicYear: fullClassData.feesPerAcademicYear,
          numberOfTerms: fullClassData.numberOfTerms,
          termFees: fullClassData.termFees || []
        });
      }
    } else {
      setSelectedClassFees(null);
    }
  };

  useEffect(() => {
    (async () => {
      console.log('User token:', user?.token ? 'present' : 'missing');
      console.log('User object:', user);
      const [studentsData, classesData] = await Promise.all([
        studentService.getAllStudents(user.token),
        classService.getAllClasses(user.token)
      ]);
      console.log('Fetched students:', studentsData);
      console.log('Fetched classes:', classesData);
      setStudents(studentsData);
      setClasses(classesData);
    })();
  }, [user.token]); // eslint-disable-line react-hooks/exhaustive-deps

  // No auto-initialization - show all students by default

  // Handle page refresh with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isModalOpen && hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isModalOpen, hasUnsavedChanges]);

  // Add history state when modal opens
  useEffect(() => {
    if (isModalOpen && !historyStateAdded) {
      console.log('Adding history state for modal');
      window.history.pushState({ modalOpen: true }, '', window.location.href);
      setHistoryStateAdded(true);
    } else if (!isModalOpen && historyStateAdded) {
      console.log('Removing history state for modal');
      setHistoryStateAdded(false);
    }
  }, [isModalOpen, historyStateAdded]);

  // Handle back button navigation with unsaved changes
  useEffect(() => {
    const handlePopState = (e) => {
      console.log('PopState event triggered', { isModalOpen, hasChanges: hasUnsavedChanges() });
      if (isModalOpen) {
        if (hasUnsavedChanges()) {
          console.log('Showing confirmation modal for back button');
          // Show our custom confirmation modal
          setPendingAction('back');
          setShowUnsavedChangesModal(true);
          // Push the state back to prevent navigation
          window.history.pushState({ modalOpen: true }, '', window.location.href);
        } else {
          console.log('No unsaved changes, closing modal');
          // If no unsaved changes, allow navigation by closing modal
          setIsModalOpen(false);
          resetForm();
        }
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isModalOpen, hasUnsavedChanges]);


  const filteredStudents = students.filter(student => {
    // Text search filter
    const matchesSearch = !searchTerm ||
      student?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      student?.email?.toLowerCase().includes(searchTerm?.toLowerCase());

    // Class filter
    const matchesClass = !selectedClassFilter ||
      (student?.class && (
        (typeof student.class === 'object' && student.class._id === selectedClassFilter) ||
        (typeof student.class === 'string' && student.class === selectedClassFilter)
      ));

    // Gender filter
    const matchesGender = !selectedGenderFilter ||
      student?.gender?.toLowerCase() === selectedGenderFilter.toLowerCase();

    // Academic year filter - check if student's class belongs to the selected academic year
    const matchesAcademicYear = !selectedAcademicYearFilter ||
      (student?.class && classes.some(cls => {
        if (!cls.academicYearFrom || !cls.academicYearTo) return false;
        const fromYear = new Date(cls.academicYearFrom).getFullYear();
        const toYear = new Date(cls.academicYearTo).getFullYear();
        const classAcademicYear = `${fromYear}-${toYear}`;
        return classAcademicYear === selectedAcademicYearFilter &&
          (cls._id === student.class?._id || cls._id === student.class);
      }));

    console.log(`Student ${student?.name}: search=${matchesSearch}, class=${matchesClass}, gender=${matchesGender}, academicYear=${matchesAcademicYear}`);

    return matchesSearch && matchesClass && matchesGender && matchesAcademicYear;
  }).sort((a, b) => {
    // Name sorting
    if (nameSort === 'a-z') {
      return (a?.name || '').localeCompare(b?.name || '');
    } else {
      return (b?.name || '').localeCompare(a?.name || '');
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredStudents?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedStudents = filteredStudents?.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedClassFilter, selectedGenderFilter, selectedAcademicYearFilter, nameSort]);

  // Function to handle modal close attempts
  const handleModalCloseAttempt = (action) => {
    if (hasUnsavedChanges()) {
      setPendingAction(action);
      setShowUnsavedChangesModal(true);
    } else {
      executeAction(action);
    }
  };

  // Function to execute the pending action
  const executeAction = (action) => {
    switch (action) {
      case 'close':
        setIsModalOpen(false);
        resetForm();
        break;
      case 'cancel':
        setIsModalOpen(false);
        resetForm();
        break;
      case 'refresh':
        window.location.reload();
        break;
      case 'back':
        window.history.back();
        break;
      default:
        break;
    }
  };

  // Function to confirm and proceed with action
  const confirmAction = () => {
    executeAction(pendingAction);
    setShowUnsavedChangesModal(false);
    setPendingAction(null);
  };

  // Function to cancel the action
  const cancelAction = () => {
    setShowUnsavedChangesModal(false);
    setPendingAction(null);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '+91',
      class: '',
      academicYear: '',
      dateOfBirth: '',
      contactPerson: [{ relation: '', phone: '+91', name: '' }],
      gender: 'male',
      motherLanguage: '',
      nationality: 'INDIA',
      aadharNumber: '',
      birthCertificateNumber: '',
      bloodGroup: '',
      address: '',
      password: ''
    });
    setImagePreview(null);
    setValidationErrors({});
    setSelectedClassFees(null);
  };

  const getPasswordPattern = () => {
    const contact = formData.contactPerson?.[0];
    const dob = formData.dateOfBirth;
    const phone = formData.phone;

    if (!contact?.name || !dob || !phone) {
      return null;
    }

    const firstTwo = contact.name.trim().substring(0, 2).toUpperCase();
    const [year, month, day] = dob.split('-');
    const formattedDob = `${year}${month}${day}`;
    const cleanedPhone = phone.trim().replace(/\s/g, '');
    const lastTwoPhone = cleanedPhone.slice(-2);

    return `${firstTwo}@${formattedDob}${lastTwoPhone}`;
  };

  const generatePassword = () => {
    const generated = getPasswordPattern();
    if (!generated) {
      error('Please fill Contact Person Name, Date of Birth, and Phone first');
      return;
    }
    setFormData(prev => ({ ...prev, password: generated }));
    success('Password generated: ' + generated);
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-()]/g, ''));
  };

  const validateAadhar = (aadhar = "") => {
    // Ensure it's a string and remove spaces or dashes
    const cleanAadhar = String(aadhar).trim().replace(/[\s-]/g, '');

    // Aadhaar must be exactly 12 digits and start with 2–9
    const aadharRegex = /^[2-9][0-9]{11}$/;

    return aadharRegex.test(cleanAadhar);
  };

  const validateBirthCertificateNumber = (certNumber) => {
    // Birth certificate number should be alphanumeric and at least 5 characters
    const cleanCertNumber = certNumber.trim();
    return cleanCertNumber.length >= 5;
  };

  const validateForm = () => {
    const errors = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 3) {
      errors.name = 'Name must be at least 3 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }

    // Academic Year validation
    if (!formData.academicYear) {
      errors.academicYear = 'Academic year selection is required';
    }

    // Class validation
    if (!formData.class) {
      errors.class = 'Class selection is required';
    }

    // Date of birth validation
    if (!formData.dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required';
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      if (birthDate >= today) {
        errors.dateOfBirth = 'Date of birth must be in the past';
      }
    }

    // Mother language validation
    if (!formData.motherLanguage.trim()) {
      errors.motherLanguage = 'Mother language is required';
    }

    // Nationality validation
    if (!formData.nationality.trim()) {
      errors.nationality = 'Nationality is required';
    }

    // Blood group validation
    if (!formData.bloodGroup) {
      errors.bloodGroup = 'Blood group is required';
    }

    // Aadhar validation (optional)
    if (formData.aadharNumber.trim() && !validateAadhar(formData.aadharNumber)) {
      errors.aadharNumber = 'Please enter a valid 12-digit Aadhar number';
    }

    // Birth Certificate Number validation    // Birth certificate validation
    if (formData.birthCertificateNumber.trim() && !validateBirthCertificateNumber(formData.birthCertificateNumber)) {
      errors.birthCertificateNumber = 'Birth certificate number must be at least 5 characters';
    }

    // Contact person validation
    if (!formData.contactPerson || formData.contactPerson.length === 0) {
      errors.contactPerson = 'At least one contact person is required';
    } else {
      const contactErrors = [];
      formData.contactPerson.forEach((contact, index) => {
        const contactError = {};
        if (!contact.name.trim()) contactError.name = 'Contact name is required';
        if (!contact.relation) contactError.relation = 'Relation is required';
        if (!contact.phone.trim() || contact.phone.trim().length <= 4) contactError.phone = 'Valid phone is required';
        if (Object.keys(contactError).length > 0) {
          contactErrors[index] = contactError;
        }
      });
      if (contactErrors.length > 0) {
        errors.contactPerson = contactErrors;
      }
    }

    // Address validation
    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }


    setValidationErrors(errors);
    console.log(errors, 'errors')
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    let finalPassword = formData.password;
    if (!finalPassword) {
      finalPassword = getPasswordPattern();
      if (!finalPassword) {
        error('Please fill Contact Person Name, Date of Birth, and Phone for auto-password generation, or enter a password manually.');
        return;
      }
    }

    const api = new APIRequest();
    const payload = new FormData();

    payload.append('name', formData.name);
    payload.append('email', formData.email);
    payload.append('phone', formData.phone);
    payload.append('class', JSON.stringify(formData.class));
    payload.append('academicYear', formData.academicYear);
    payload.append('dateOfBirth', formData.dateOfBirth);
    payload.append('contactPerson', JSON.stringify(formData.contactPerson));
    payload.append('gender', formData.gender);
    payload.append('motherLanguage', formData.motherLanguage);
    payload.append('nationality', formData.nationality);
    payload.append('aadharNumber', formData.aadharNumber);
    payload.append('birthCertificateNumber', formData.birthCertificateNumber);
    payload.append('bloodGroup', formData.bloodGroup);
    payload.append('address', formData.address);
    payload.append('password', finalPassword);
    payload.append('customFields', JSON.stringify(formData.customFields || {})); // NEW: Include custom fields
    payload.append('role', 'student');

    if (formData.image) {
      payload.append('image', formData.image);
    }

    try {
      const response = await api.request('/admin/createStudent', 'POST', payload, user.token);
      if (response.success) {
        success(response.message || 'Student added successfully!');
        setIsModalOpen(false);
        resetForm();
        const updatedClasses = await studentService.getAllStudents(user.token);
        setStudents(updatedClasses);
      } else {
        error('Failed to add student. Please try again.');
      }
    } catch (err) {
      console.error('Error adding student:', err);
      error('Failed to add student. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleView = async (student) => {
    setIsCreating(true);
    try {
      const api = new APIRequest();
      const response = await api.request(`/admin/getStudent?id=${student._id}`, 'GET', {}, user.token);

      if (response.success) {
        navigate(`/students/${student._id}`, { state: { studentData: response.student } });
      } else {
        error(response.message || 'Failed to fetch student details. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching student data:', err);
      error('Failed to fetch student details. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleEdit = async (student) => {
    setIsCreating(true);
    try {
      const api = new APIRequest();
      const response = await api.request(`/admin/getStudent?id=${student._id}`, 'GET', {}, user.token);

      if (response.success) {
        navigate(`/students/${student._id}/edit`, { state: { studentData: response.student } });
      } else {
        error(response.message || 'Failed to fetch student details. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching student data:', err);
      error('Failed to fetch student details. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        const response = await studentService.deleteStudent(id, user.token);
        if (response.success) {
          setStudents(students.filter(s => (s.id || s._id) !== id));
          success('Student deleted successfully');
        } else {
          error(response.message || 'Failed to delete student');
        }
      } catch (err) {
        console.error('Error deleting student:', err);
        error('Failed to delete student');
      }
    }
  };

  const handleStatusToggle = async () => {
    if (!studentToToggle) return;

    try {
      const api = new APIRequest();
      const response = await api.request(`/admin/updateStudentStatus?id=${studentToToggle.id || studentToToggle._id}`, 'POST', {
        status: targetStatus
      }, user.token);

      if (response.success) {
        setStudents(students.map(s => 
          (s.id === studentToToggle.id || s._id === studentToToggle._id) ? { ...s, status: targetStatus } : s
        ));
        success(`Student status updated to ${targetStatus}`);
      } else {
        error(response.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      error('An error occurred while updating status');
    } finally {
      setIsStatusModalOpen(false);
      setStudentToToggle(null);
    }
  };

  const addContactPerson = () => {
    setFormData({
      ...formData,
      contactPerson: [...(formData.contactPerson || []), { relation: '', phone: '', name: '' }]
    });
  };

  const removeContactPerson = (index) => {
    const updated = formData.contactPerson?.filter((_, i) => i !== index) || [];
    setFormData({ ...formData, contactPerson: updated });
  };

  const updateContactPerson = (index, field, value) => {
    let finalValue = value;
    if (field === 'phone') {
      // Ensure +91 prefix for phone numbers
      if (value && !value.startsWith('+91') && !value.startsWith('+')) {
        finalValue = '+91' + value;
      } else if (value && value.startsWith('+') && !value.startsWith('+91')) {
        finalValue = '+91' + value.substring(1);
      } else if (!value) {
        finalValue = '+91';
      }
    }
    const updated = formData.contactPerson?.map((cp, i) =>
      i === index ? { ...cp, [field]: finalValue } : cp
    ) || [];
    setFormData({ ...formData, contactPerson: updated });
  };


  const columns = [
    {
      key: 'name',
      label: 'Student',
      render: (student) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 overflow-hidden text-[#1e293b]">
            {student.image ? (
              <img
                src={`/${student.image}`}
                alt={student.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            ) : (
              <span className="text-blue-600 font-semibold text-sm">
                {student.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </span>
            )}
          </div>
          <div>
            <p className="font-medium text-[#1e293b]">{student.name}</p>
            <p className="text-sm text-slate-500 font-medium">{student.email}</p>
          </div>
        </div>
      )
    },
    {
      key: 'class',
      label: 'Class',
      render: (student) => {
        const studentClass = student?.class;
        if (studentClass && typeof studentClass === 'object') {
          return (
            <span className="px-3 py-1 bg-blue-50 text-[#1B6CA8] text-xs font-bold rounded-full">
              {studentClass.name} - {studentClass.section}
            </span>
          );
        } else if (typeof studentClass === 'string') {
          return (
            <span className="px-3 py-1 bg-blue-50 text-[#1B6CA8] text-xs font-bold rounded-full">
              {studentClass}
            </span>
          );
        }
        return <span className="text-slate-400 text-sm font-medium italic">Not assigned</span>;
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (student) => (
        <button
          onClick={() => {
            setStudentToToggle(student);
            setTargetStatus(student.status === 'inactive' ? 'active' : 'inactive');
            setIsStatusModalOpen(true);
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
            student.status === 'active' || !student.status ? 'bg-green-500' : 'bg-slate-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
              student.status === 'active' || !student.status ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      )
    },
    {
      key: 'regNo',
      label: 'Reg Number',
      render: (student) => (
        <span className="font-bold text-[#1e293b]">{student.regNo || 'N/A'}</span>
      )
    },
    {
      key: 'gender',
      label: 'Gender',
      render: (student) => (
        <span className="capitalize font-semibold text-slate-600 px-3 py-1 bg-slate-100 rounded-full text-[11px]">{student.gender}</span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (student) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleView(student)}
            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-300"
            title="View Details"
          >
            <Eye size={18} />
          </button>
          <button
            onClick={() => handleEdit(student)}
            className="p-2 text-slate-400 hover:text-[#1B6CA8] hover:bg-blue-50 rounded-lg transition-all duration-300"
            title="Edit"
          >
            <Edit2 size={18} />
          </button>
          <button
            onClick={() => handleDelete(student._id || student.id)}
            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300"
            title="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>
      )
    }
  ];

  // Loading state
  if (isCreating) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // List View
  if (viewMode === 'list') {
    return (
      <div className="space-y-8 animate-fadeIn max-w-[1600px] mx-auto">
        {/* Modern Header Section */}
        <div className="glass-card rounded-[2.5rem] p-8 px-10 flex flex-col md:flex-row justify-between items-center gap-6 premium-shadow relative z-50">
          <div className="text-center md:text-left">
            <h1 className="text-4xl font-black tracking-tight text-[#1e293b]">
              <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">Students</span>
            </h1>
            <p className="text-slate-500 mt-2 font-medium flex items-center justify-center md:justify-start gap-2">
              <Users size={18} className="text-[#1B6CA8]" />
              Manage student information and enrollment
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex bg-slate-100 rounded-2xl p-1 shadow-sm">
              <button
                onClick={() => {
                  const columns = [
                    { header: 'Name', dataKey: 'name' },
                    { header: 'Class', dataKey: 'className' },
                    { header: 'Section', dataKey: 'section' },
                    { header: 'Reg No', dataKey: 'regNo' },
                    { header: 'Email', dataKey: 'email' },
                    { header: 'Phone', dataKey: 'phone' }
                  ];
                  const data = filteredStudents.map(s => ({
                    ...s,
                    className: s.class?.name || s.class || 'N/A',
                    section: s.class?.section || 'N/A'
                  }));
                  ReportService.generatePDF('Student Directory', columns, data, 'student_directory.pdf');
                }}
                className="px-4 py-2.5 text-[#1e293b] hover:bg-white rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm"
              >
                <Download size={16} className="text-blue-600" />
                <span>PDF</span>
              </button>
              <button
                onClick={() => {
                  const data = filteredStudents.map(s => ({
                    Name: s.name,
                    Class: s.class?.name || s.class || 'N/A',
                    Section: s.class?.section || 'N/A',
                    'Reg No': s.regNo,
                    Email: s.email,
                    Phone: s.phone,
                    Gender: s.gender,
                    DOB: s.dateOfBirth
                  }));
                  ReportService.generateExcel(data, 'student_directory.xlsx');
                }}
                className="px-4 py-2.5 text-[#1e293b] hover:bg-white rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm"
              >
                <Download size={16} className="text-emerald-600" />
                <span>Excel</span>
              </button>
            </div>
            <button
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
              className="primary-gradient-bg text-white px-8 py-3.5 rounded-2xl hover:opacity-90 transition-all duration-300 flex items-center gap-3 font-bold shadow-lg shadow-blue-200/50 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Plus size={24} />
              <span>Add Student</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="glass-card rounded-[2rem] p-8 premium-shadow border-none animate-slideInUp relative z-0">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-8 gap-4">
            <div>
              <h3 className="text-xl font-black text-slate-800 tracking-tight">Filters</h3>
              <p className="text-slate-400 text-sm font-medium">Refine your student directory view</p>
            </div>
            <div className="px-4 py-2 bg-blue-50 rounded-full">
              <span className="text-sm font-bold text-blue-600">
                {filteredStudents?.length || 0} <span className="text-slate-400 font-medium">of</span> {students?.length || 0} <span className="text-slate-400 font-medium">students</span>
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
            {/* Search Filter */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Search Name/Email</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all duration-300 font-semibold text-slate-700"
                />
              </div>
            </div>
            {/* Academic Year Filter */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Academic Year</label>
              <select
                value={selectedAcademicYearFilter}
                onChange={(e) => setSelectedAcademicYearFilter(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all duration-300 font-semibold text-slate-700 cursor-pointer"
              >
                <option value="">All Years</option>
                {getUniqueAcademicYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Class Filter */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Class</label>
              <SearchableDropdown
                options={getClassesForSelectedAcademicYear()}
                value={getClassesForSelectedAcademicYear().find(option => option._id === selectedClassFilter) || ''}
                onChange={(selectedClass) => setSelectedClassFilter(selectedClass?._id || '')}
                placeholder={selectedAcademicYearFilter ? "Select Class" : "Select Academic Year First"}
                placeholderClassName={!selectedAcademicYearFilter ? "text-[10px]" : ""}
                label=""
                searchPlaceholder="Search classes..."
                noResultsText="No classes found"
                disabled={!selectedAcademicYearFilter}
              />
            </div>

            {/* Gender Filter */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Gender</label>
              <select
                value={selectedGenderFilter}
                onChange={(e) => setSelectedGenderFilter(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all duration-300 font-semibold text-slate-700 cursor-pointer"
              >
                <option value="">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Name Sort Filter */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Sort Order</label>
              <select
                value={nameSort}
                onChange={(e) => setNameSort(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all duration-300 font-semibold text-slate-700 cursor-pointer"
              >
                <option value="a-z">Name (A to Z)</option>
                <option value="z-a">Name (Z to A)</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex flex-col xl:col-start-6">
              <label className="text-[11px] font-black text-transparent uppercase tracking-widest mb-3 hidden xl:block">Clear</label>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedClassFilter('');
                  setSelectedGenderFilter('');
                  setSelectedAcademicYearFilter('');
                  setNameSort('a-z');
                }}
                className="w-full px-4 py-3 text-slate-500 font-bold bg-slate-100/50 rounded-xl hover:bg-slate-200/50 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <X size={18} />
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end mb-4">
          <ItemsPerPageSelector 
            value={itemsPerPage} 
            onChange={handleItemsPerPageChange} 
          />
        </div>

        <Table
          data={paginatedStudents}
          columns={columns}
          searchable={false}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          emptyMessage="No students found"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={filteredStudents?.length}
        />

        <ConfirmModal
          isOpen={isStatusModalOpen}
          onClose={() => {
            setIsStatusModalOpen(false);
            setStudentToToggle(null);
          }}
          onConfirm={handleStatusToggle}
          title="Change Student Status"
          message={`Are you sure you want to change this student's status to ${targetStatus}? This will ${targetStatus === 'inactive' ? 'restrict' : 'restore'} their access.`}
          confirmText="Yes, Change Status"
          cancelText="No, Keep as is"
          type={targetStatus === 'inactive' ? 'danger' : 'warning'}
        />

        {/* Add Student Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => handleModalCloseAttempt('close')}
          title="Add New Student"
          size="xl"
          preventBackdropClose={true}
        >
          <form onSubmit={handleSubmit} className="space-y-8 p-1">

              {/* Image Upload */}
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-24 h-24 bg-blue-50 rounded-2xl border-2 border-dashed border-blue-200 flex items-center justify-center overflow-hidden flex-shrink-0 relative group">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : formData.image && typeof formData.image === 'string' ? (
                    <img src={`/${formData.image}`} alt="Current" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-blue-400 flex flex-col items-center">
                      <Plus size={24} />
                      <span className="text-[10px] font-bold uppercase mt-1">Photo</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                </div>
                <div className="flex-1 w-full">
                  <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Profile Image</label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setFormData({ ...formData, image: file });
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setImagePreview(reader.result);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="hidden"
                      id="student-image-upload"
                    />
                    <label
                      htmlFor="student-image-upload"
                      className="inline-flex items-center px-4 py-2.5 bg-white border-2 border-slate-100 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 hover:border-blue-400 hover:text-blue-600 transition-all duration-300 cursor-pointer shadow-sm"
                    >
                      <Plus size={18} className="mr-2" />
                      Choose Photo
                    </label>
                    <p className="text-[11px] font-medium text-slate-400 mt-2 italic">
                      Recommended: Square JPG/PNG, Max 10MB
                    </p>
                  </div>
                </div>
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter student full name"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${validationErrors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    required
                  />
                  {validationErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="example@email.com"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${validationErrors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    required
                  />
                  {validationErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <PhoneInput
                    label="Phone *"
                    value={formData.phone}
                    onChange={(val) => setFormData({ ...formData, phone: val })}
                    error={validationErrors.phone}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year *</label>
                  <select
                    value={formData.academicYear}
                    onChange={(e) => {
                      setFormData({ ...formData, academicYear: e.target.value, class: '' });
                      setSelectedClassFees(null);
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${validationErrors.academicYear ? 'border-red-500' : 'border-gray-300'
                      }`}
                    required
                  >
                    <option value="">Select Academic Year</option>
                    {getUniqueAcademicYears().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  {validationErrors.academicYear && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.academicYear}</p>
                  )}
                </div>
                <div>
                  <SearchableDropdown
                    options={getFilteredClasses()}
                    value={getFilteredClasses().find(option => option._id === (formData.class?._id || formData.class)) || ''}
                    onChange={handleClassSelection}
                    placeholder={formData.academicYear ? "Select a class" : "Select academic year first"}
                    label="Class"
                    searchPlaceholder="Search classes..."
                    noResultsText="No classes found"
                    disabled={!formData.academicYear}
                  />
                  {validationErrors.class && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.class}</p>
                  )}
                </div>
              </div>

              {/* Class Fees Structure Display */}
              {selectedClassFees && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">Class Fees Structure</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">Total Fees per Academic Year</label>
                      <p className="text-lg font-bold text-blue-900">
                        ₹{selectedClassFees.feesPerAcademicYear?.toLocaleString('en-IN') || '0'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">Number of Terms</label>
                      <p className="text-lg font-bold text-blue-900">
                        {selectedClassFees.numberOfTerms || 0} Terms
                      </p>
                    </div>
                  </div>

                  {selectedClassFees.termFees && selectedClassFees.termFees.length > 0 && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-blue-700 mb-2">Term-wise Fees Breakdown</label>
                      <div className="space-y-2">
                        {selectedClassFees.termFees.map((term, index) => (
                          <div key={index} className="flex justify-between items-center bg-white rounded-lg p-3 border border-blue-200">
                            <div>
                              <span className="font-medium text-blue-900">Term {index + 1}</span>
                              {term.dueDate && (
                                <span className="text-sm text-blue-600 ml-2">
                                  (Due: {new Date(term.dueDate).toLocaleDateString('en-IN')})
                                </span>
                              )}
                            </div>
                            <span className="font-bold text-blue-900">
                              ₹{term.amount?.toLocaleString('en-IN') || '0'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <CustomDatePicker
                    label="Date of Birth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                    required={true}
                    validationError={validationErrors.dateOfBirth}
                    maxYear={new Date().getFullYear()}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${validationErrors.gender ? 'border-red-500' : 'border-gray-300'
                      }`}
                    required
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  {validationErrors.gender && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.gender}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mother Tongue *</label>
                  <input
                    type="text"
                    value={formData.motherLanguage}
                    onChange={(e) => setFormData({ ...formData, motherLanguage: e.target.value })}
                    placeholder="Enter mother tongue"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${validationErrors.motherLanguage ? 'border-red-500' : 'border-gray-300'
                      }`}
                    required
                  />
                  {validationErrors.motherLanguage && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.motherLanguage}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nationality *</label>
                  <input
                    type="text"
                    value={formData.nationality}
                    onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                    placeholder="Enter nationality"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${validationErrors.nationality ? 'border-red-500' : 'border-gray-300'
                      }`}
                    required
                  />
                  {validationErrors.nationality && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.nationality}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Blood Group *</label>
                  <select
                    value={formData.bloodGroup}
                    onChange={(e) => setFormData({ ...formData, bloodGroup: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${validationErrors.bloodGroup ? 'border-red-500' : 'border-gray-300'
                      }`}
                    required
                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                  {validationErrors.bloodGroup && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.bloodGroup}</p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="44, roadName, city, state, pincode"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${validationErrors.address ? 'border-red-500' : 'border-gray-300'
                    }`}
                  rows={3}
                  required
                />
                {validationErrors.address && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.address}</p>
                )}
              </div>


              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Aadhar Number (Optional)</label>
                  <input
                    type="text"
                    value={formData.aadharNumber}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                      setFormData({ ...formData, aadharNumber: val });
                    }}
                    placeholder="12-digit Aadhar number"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${validationErrors.aadharNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {validationErrors.aadharNumber && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.aadharNumber}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Birth Certificate Number (Optional)</label>
                  <input
                    type="text"
                    value={formData.birthCertificateNumber}
                    onChange={(e) => setFormData({ ...formData, birthCertificateNumber: e.target.value.toUpperCase() })}
                    placeholder="Enter Certificate Number"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${validationErrors.birthCertificateNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {validationErrors.birthCertificateNumber && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.birthCertificateNumber}</p>
                  )}
                </div>
              </div>

              {/* Dynamic Custom Fields */}
              {configFields.length > 0 && (
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 border-b pb-2">Additional Information</h4>
                  <DynamicFields
                    fields={configFields}
                    formData={formData.customFields || {}}
                    onChange={handleCustomFieldChange}
                    validationErrors={validationErrors}
                  />
                </div>
              )}

              {/* Contact Persons */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">Contact Persons *</label>
                  <button
                    type="button"
                    onClick={addContactPerson}
                    className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                  >
                    <Plus size={16} />
                    <span>Add Contact</span>
                  </button>
                </div>
                {formData.contactPerson?.map((contact, index) => (
                  <div key={index} className="relative bg-slate-50/50 p-6 rounded-[2rem] border border-slate-100 mb-4 transition-all duration-300 hover:shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-y-6 gap-x-8 items-start">
                      {/* Name Field */}
                      <div className="md:col-span-4">
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Full Name</label>
                        <input
                          type="text"
                          placeholder="e.g. John Doe"
                          value={contact.name}
                          onChange={(e) => updateContactPerson(index, 'name', e.target.value)}
                          className={`w-full px-4 py-3 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all ${
                            validationErrors.contactPerson?.[index]?.name ? 'border-red-500' : 'border-slate-200'
                          }`}
                        />
                        {validationErrors.contactPerson?.[index]?.name && (
                          <p className="mt-1 text-xs text-red-500 ml-1">{validationErrors.contactPerson[index].name}</p>
                        )}
                      </div>

                      {/* Relation Field */}
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Relation</label>
                        <div className="relative">
                          <select
                            value={contact.relation}
                            onChange={(e) => updateContactPerson(index, 'relation', e.target.value)}
                            className={`w-full px-4 py-3 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all cursor-pointer appearance-none ${
                              validationErrors.contactPerson?.[index]?.relation ? 'border-red-500' : 'border-slate-200'
                            }`}
                          >
                            <option value="">Select Relation</option>
                            <option value="Father">Father</option>
                            <option value="Mother">Mother</option>
                            <option value="Brother">Brother</option>
                            <option value="Sister">Sister</option>
                            <option value="Grandfather">Grandfather</option>
                            <option value="Grandmother">Grandmother</option>
                            <option value="Guardian">Guardian</option>
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                        </div>
                        {validationErrors.contactPerson?.[index]?.relation && (
                          <p className="mt-1 text-xs text-red-500 ml-1">{validationErrors.contactPerson[index].relation}</p>
                        )}
                      </div>

                      {/* Phone Field */}
                      <div className="md:col-span-4">
                        <PhoneInput
                          label="Phone"
                          value={contact.phone}
                          onChange={(val) => updateContactPerson(index, 'phone', val)}
                          error={validationErrors.contactPerson?.[index]?.phone}
                        />
                      </div>

                      {/* Remove Button */}
                      <div className="md:col-span-1 flex justify-center pt-8">
                        {formData.contactPerson.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeContactPerson(index)}
                            className="p-2.5 bg-white border border-slate-100 text-slate-400 hover:text-red-500 hover:border-red-100 hover:bg-red-50 rounded-full transition-all duration-300 shadow-sm"
                            title="Remove Contact"
                          >
                            <X size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {typeof validationErrors.contactPerson === 'string' && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.contactPerson}</p>
                )}
              </div>
              {/* Login Credentials Section */}
              <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Mobile Login Credentials</h4>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="text-xs font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 transition-all hover:scale-105 active:scale-95 flex items-center gap-2"
                  >
                    <Settings size={14} className="animate-spin-slow" />
                    Auto-generate Password
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Login ID (Reg Number)</label>
                    <input
                      type="text"
                      value={formData.regNo || 'Auto-generated on save'}
                      disabled
                      className="w-full px-4 py-3 bg-slate-200/50 border border-slate-200 rounded-xl text-slate-500 font-bold cursor-not-allowed"
                    />
                    <p className="mt-1 text-[10px] text-slate-400 ml-1 italic">Students use their Registration Number to log in.</p>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Login Password *</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Enter or generate password"
                        className={`w-full px-4 py-3 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all pr-12 ${
                          validationErrors.password ? 'border-red-500' : 'border-slate-200'
                        }`}
                      />
                      {formData.password && (
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-blue-600 transition-colors"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      )}
                    </div>
                    {validationErrors.password && (
                      <p className="mt-1 text-xs text-red-500 ml-1">{validationErrors.password}</p>
                    )}
                  </div>
                </div>
              </div>

            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => handleModalCloseAttempt('cancel')}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Student

              </button>
            </div>
          </form>
        </Modal>

        {/* Unsaved Changes Confirmation Modal */}
        <Modal
          isOpen={showUnsavedChangesModal}
          onClose={cancelAction}
          title="Unsaved Changes"
          size="md"
        >
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">You have unsaved changes</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Your form data will be lost if you continue. Are you sure you want to proceed?
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={cancelAction}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Continue Editing
              </button>
              <button
                onClick={confirmAction}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Discard Changes
              </button>
            </div>
          </div>
        </Modal>

      </div>
    );
  }
};

export default Students;